import { z } from "zod";

import { StudentNumberSchema, UuidSchema } from "./domain.js";

/** The teacher types this over the cover; it is the whole instruction. */
export const MAX_LESSON_MESSAGE = 60;
/** The migration's column default; six groups is the common classroom. */
export const DEFAULT_GROUP_COUNT = 6;
export const MIN_GROUP_COUNT = 1;
export const MAX_GROUP_COUNT = 12;
/** A cover timer longer than one period is a mistake, not an intent. */
export const MAX_LOCK_SECONDS = 3_600;

/**
 * OFF   nothing is covered.
 * SCREEN a solid cover; the class looks at the teacher.
 */
export const LockModeSchema = z.enum(["OFF", "SCREEN"]);

const uniqueNumbers = (values: readonly number[]) =>
  new Set(values).size === values.length;

export const LessonLockSchema = z
  .object({
    mode: LockModeSchema,
    message: z.string().max(MAX_LESSON_MESSAGE),
    endsAt: z.string().datetime().nullable(),
    /** Students released early while the rest stay covered. */
    exemptStudentNumbers: z
      .array(StudentNumberSchema)
      .refine(uniqueNumbers, "Duplicate student number"),
  })
  .strict();

export const CompletionModeSchema = z.enum(["GROUP", "INDIVIDUAL"]);

export const CompletionSchema = z
  .object({
    mode: CompletionModeSchema,
    /** One-based slot numbers: groups in GROUP mode, students in INDIVIDUAL. */
    checked: z
      .array(z.number().int().min(1).max(64))
      .refine(uniqueNumbers, "Duplicate slot"),
  })
  .strict();

const lessonStateShape = (canvasIdSchema: z.ZodType<string>) =>
  z
    .object({
      canvasId: canvasIdSchema,
      lock: LessonLockSchema,
      completion: CompletionSchema,
      groupCount: z.number().int().min(MIN_GROUP_COUNT).max(MAX_GROUP_COUNT),
      /** Whether students may write or delete comments on sealed frames
       * right now. Teacher-set, canvas-wide, and independent of group
       * count — a changed group count must not silently lock it again. */
      studentComments: z.boolean(),
    })
    .strict();

export const LessonStateSchema = lessonStateShape(UuidSchema);

export const createScopedLessonStateSchema = (expectedCanvasId: string) =>
  lessonStateShape(z.literal(expectedCanvasId));

export const LockRequestSchema = z
  .object({
    canvasId: UuidSchema,
    mode: LockModeSchema,
    message: z.string().max(MAX_LESSON_MESSAGE),
    durationSeconds: z.number().int().min(1).max(MAX_LOCK_SECONDS).nullable(),
    exemptStudentNumbers: z
      .array(StudentNumberSchema)
      .refine(uniqueNumbers, "Duplicate student number"),
  })
  .strict();

export const CompletionToggleSchema = z
  .object({
    canvasId: UuidSchema,
    slot: z.number().int().min(1).max(64),
    checked: z.boolean(),
  })
  .strict();

export const CompletionModeRequestSchema = z
  .object({ canvasId: UuidSchema, mode: CompletionModeSchema })
  .strict();

export const CompletionResetSchema = z
  .object({ canvasId: UuidSchema })
  .strict();

export const StudentCommentsRequestSchema = z
  .object({ canvasId: UuidSchema, open: z.boolean() })
  .strict();

export type LockMode = z.infer<typeof LockModeSchema>;
export type LessonLock = z.infer<typeof LessonLockSchema>;
export type CompletionMode = z.infer<typeof CompletionModeSchema>;
export type Completion = z.infer<typeof CompletionSchema>;
export type LessonState = z.infer<typeof LessonStateSchema>;
export type LockRequest = z.infer<typeof LockRequestSchema>;
export type CompletionToggle = z.infer<typeof CompletionToggleSchema>;
export type CompletionModeRequest = z.infer<typeof CompletionModeRequestSchema>;
export type CompletionReset = z.infer<typeof CompletionResetSchema>;
export type StudentCommentsRequest = z.infer<
  typeof StudentCommentsRequestSchema
>;
